import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Typography, Button, CircularProgress, IconButton, Tooltip,
    Breadcrumbs, Link as MuiLink, Alert, ThemeProvider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useNotify } from '../../components/Datatable/NotifyProvider';
import lightTheme from '../../themes/lightTheme';
import MarkdownRender from '../../components/Markdown';

/**
 * Página de documentação da API Feed — busca docs/api/feed.md no backend
 * e renderiza com um parser markdown ultra-básico (sem dep externa).
 * Cobre o essencial: h1-h3, parágrafos, code blocks ``` fenced, code inline,
 * listas, tabelas, links, separadores.
 */
export default function ApiDocsPage() {
    const navigate = useNavigate();
    const notify = useNotify();
    const [text, setText] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        document.title = 'IPV7 Tools - Documentação API';
        api.get('/feed/docs/markdown', { responseType: 'text', transformResponse: [(d) => d] })
            .then((res) => {
                setText(res.data || '');
                setLoading(false);
            })
            .catch((e) => {
                setError(e.response?.data || 'Erro ao carregar a documentação');
                setLoading(false);
            });
    }, []);

    return (
        <ThemeProvider theme={lightTheme}>
        <Box sx={{ bgcolor: 'background.default', color: 'text.primary', minHeight: 'calc(100vh - 100px)', p: { xs: 1, md: 2 } }}>
            <Breadcrumbs sx={{ mb: 2 }}>
                <MuiLink component="button" onClick={() => navigate('/api_tokens')}>
                    Tokens API
                </MuiLink>
                <Typography color="text.primary">Documentação</Typography>
            </Breadcrumbs>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={() => navigate('/api_tokens')}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h5" sx={{ fontFamily: 'Montserrat', fontWeight: 300 }}>
                        Documentação da API
                    </Typography>
                </Box>
                <Button
                    startIcon={<OpenInNewIcon />}
                    onClick={() => window.open(
                        (api.defaults.baseURL || '') + '/feed/docs/markdown',
                        '_blank'
                    )}
                    variant="outlined"
                    size="small"
                >
                    Ver markdown raw
                </Button>
            </Box>

            <Paper sx={{ p: { xs: 2, md: 4 }, maxWidth: 1000, mx: 'auto' }}>
                {loading && (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                )}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                )}
                {!loading && !error && <MarkdownRender text={text} />}
            </Paper>
        </Box>
        </ThemeProvider>
    );
}
